import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CreateCandidatePayload,
  CreateRequisitionPayload,
  DecisionPayload,
  ListCandidatesParams,
  ListOpeningsParams,
  ListRequisitionsParams,
  MoveApplicationPayload,
  PublishOpeningPayload,
  RECRUTAMENTO_KEYS,
  SchedulePayload,
  agendarEtapa,
  aprovarCandidatura,
  atualizarCandidato,
  atualizarRequisicao,
  atualizarVaga,
  buscarCandidato,
  buscarCandidatura,
  buscarIndicadoresFunil,
  buscarPainel,
  buscarRequisicao,
  buscarVaga,
  cancelarRequisicao,
  criarCandidato,
  criarRequisicao,
  decidirRequisicao,
  despublicarVaga,
  encerrarVaga,
  enviarCurriculo,
  enviarParaBancoTalentos,
  enviarRequisicao,
  listarAgendamentos,
  listarBancoTalentos,
  listarCandidatos,
  listarCandidaturasDoCandidato,
  listarClientesParaVaga,
  listarRecrutadores,
  listarDocumentosCandidato,
  listarEtapas,
  listarFilaAprovacoes,
  listarHistoricoCandidatura,
  listarRequisicoes,
  listarVagas,
  movimentarCandidatura,
  publicarVaga,
  reprovarCandidatura,
  revogarConsentimento,
  salvarRascunhoRequisicao,
  vincularCandidatoAVaga,
} from '../api';

/**
 * Hooks TanStack Query do módulo de Recrutamento.
 *
 * As invalidações são deliberadamente amplas (`RECRUTAMENTO_KEYS.all`)
 * nas mutations que atravessam recursos: aprovar o último passo cria uma
 * vaga, aprovar um candidato muda o status da vaga, e uma invalidação
 * cirúrgica esqueceria uma dessas pontas — o custo de recarregar é
 * pequeno diante de mostrar estado velho num fluxo de aprovação.
 */

export { RECRUTAMENTO_KEYS };

// -------------------------------------------------------------------
// Requisições
// -------------------------------------------------------------------

export function useRequisicoes(params?: ListRequisitionsParams) {
  return useQuery({
    queryKey: RECRUTAMENTO_KEYS.requisicoes(params),
    queryFn: () => listarRequisicoes(params),
  });
}

export function useRequisicao(id: string | undefined) {
  return useQuery({
    queryKey: RECRUTAMENTO_KEYS.requisicao(id ?? 'noop'),
    queryFn: () => buscarRequisicao(id as string),
    enabled: Boolean(id),
  });
}

export function useFilaAprovacoes(params?: ListRequisitionsParams) {
  return useQuery({
    queryKey: RECRUTAMENTO_KEYS.filaAprovacoes(params),
    queryFn: () => listarFilaAprovacoes(params),
  });
}

export function useCriarRequisicao() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateRequisitionPayload) => criarRequisicao(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: RECRUTAMENTO_KEYS.all }),
  });
}

export function useSalvarRascunho() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateRequisitionPayload) => salvarRascunhoRequisicao(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: RECRUTAMENTO_KEYS.all }),
  });
}

export function useAtualizarRequisicao(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Partial<CreateRequisitionPayload>) => atualizarRequisicao(id, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: RECRUTAMENTO_KEYS.all }),
  });
}

export function useEnviarRequisicao() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => enviarRequisicao(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: RECRUTAMENTO_KEYS.all }),
  });
}

export function useCancelarRequisicao() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => cancelarRequisicao(id, reason),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: RECRUTAMENTO_KEYS.all }),
  });
}

export function useDecidirRequisicao() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: DecisionPayload & { id: string }) =>
      decidirRequisicao(id, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: RECRUTAMENTO_KEYS.all }),
  });
}

// -------------------------------------------------------------------
// Vagas
// -------------------------------------------------------------------

export function useVagas(params?: ListOpeningsParams) {
  return useQuery({
    queryKey: RECRUTAMENTO_KEYS.vagas(params),
    queryFn: () => listarVagas(params),
  });
}

export function useVaga(id: string | undefined) {
  return useQuery({
    queryKey: RECRUTAMENTO_KEYS.vaga(id ?? 'noop'),
    queryFn: () => buscarVaga(id as string),
    enabled: Boolean(id),
  });
}

export function usePublicarVaga() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: PublishOpeningPayload & { id: string }) =>
      publicarVaga(id, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: RECRUTAMENTO_KEYS.all }),
  });
}

export function useAtualizarVaga() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: Partial<PublishOpeningPayload> & { id: string }) =>
      atualizarVaga(id, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: RECRUTAMENTO_KEYS.all }),
  });
}

export function useDespublicarVaga() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => despublicarVaga(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: RECRUTAMENTO_KEYS.all }),
  });
}

export function useEncerrarVaga() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => encerrarVaga(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: RECRUTAMENTO_KEYS.all }),
  });
}

// -------------------------------------------------------------------
// Candidatos
// -------------------------------------------------------------------

export function useCandidatos(params?: ListCandidatesParams) {
  return useQuery({
    queryKey: RECRUTAMENTO_KEYS.candidatos(params),
    queryFn: () => listarCandidatos(params),
  });
}

export function useBancoTalentos(params?: ListCandidatesParams) {
  return useQuery({
    queryKey: RECRUTAMENTO_KEYS.bancoTalentos(params),
    queryFn: () => listarBancoTalentos(params),
  });
}

export function useCandidato(id: string | undefined) {
  return useQuery({
    queryKey: RECRUTAMENTO_KEYS.candidato(id ?? 'noop'),
    queryFn: () => buscarCandidato(id as string),
    enabled: Boolean(id),
  });
}

export function useDocumentosCandidato(id: string | undefined) {
  return useQuery({
    queryKey: RECRUTAMENTO_KEYS.candidatoDocumentos(id ?? 'noop'),
    queryFn: () => listarDocumentosCandidato(id as string),
    enabled: Boolean(id),
  });
}

export function useCriarCandidato() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateCandidatePayload) => criarCandidato(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: RECRUTAMENTO_KEYS.all }),
  });
}

export function useAtualizarCandidato() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: Partial<CreateCandidatePayload> & { id: string }) =>
      atualizarCandidato(id, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: RECRUTAMENTO_KEYS.all }),
  });
}

export function useEnviarCurriculo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ candidateId, file }: { candidateId: string; file: File }) =>
      enviarCurriculo(candidateId, file),
    onSuccess: (_data, variables) =>
      queryClient.invalidateQueries({
        queryKey: RECRUTAMENTO_KEYS.candidatoDocumentos(variables.candidateId),
      }),
  });
}

export function useVincularCandidatoAVaga() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      candidateId,
      jobOpeningId,
      source,
    }: {
      candidateId: string;
      jobOpeningId: string;
      source?: 'interno' | 'banco_talentos';
    }) => vincularCandidatoAVaga(candidateId, jobOpeningId, source),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: RECRUTAMENTO_KEYS.all }),
  });
}

/** Clientes elegíveis para uma nova vaga — catálogo de referência. */
export function useClientesParaVaga() {
  return useQuery({
    queryKey: RECRUTAMENTO_KEYS.clientesParaVaga(),
    queryFn: listarClientesParaVaga,
    staleTime: 5 * 60 * 1000,
  });
}

export function useRecrutadores() {
  return useQuery({
    queryKey: RECRUTAMENTO_KEYS.recrutadores(),
    queryFn: listarRecrutadores,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCandidaturasDoCandidato(candidateId: string | undefined) {
  return useQuery({
    queryKey: RECRUTAMENTO_KEYS.candidatoCandidaturas(candidateId ?? 'noop'),
    queryFn: () => listarCandidaturasDoCandidato(candidateId as string),
    enabled: Boolean(candidateId),
  });
}

export function useRevogarConsentimento() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => revogarConsentimento(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: RECRUTAMENTO_KEYS.all }),
  });
}

// -------------------------------------------------------------------
// Processo seletivo
// -------------------------------------------------------------------

/** O catálogo de etapas muda por ação administrativa, não pelo uso. */
export function useEtapas() {
  return useQuery({
    queryKey: RECRUTAMENTO_KEYS.etapas(),
    queryFn: listarEtapas,
    staleTime: 5 * 60 * 1000,
  });
}

export function usePainelProcesso(jobOpeningId: string | undefined) {
  return useQuery({
    queryKey: RECRUTAMENTO_KEYS.painel(jobOpeningId ?? 'noop'),
    queryFn: () => buscarPainel(jobOpeningId as string),
    enabled: Boolean(jobOpeningId),
  });
}

export function useIndicadoresFunil(params?: Record<string, unknown>) {
  return useQuery({
    queryKey: RECRUTAMENTO_KEYS.indicadores(params),
    queryFn: () => buscarIndicadoresFunil(params),
  });
}

export function useCandidatura(id: string | undefined) {
  return useQuery({
    queryKey: RECRUTAMENTO_KEYS.candidatura(id ?? 'noop'),
    queryFn: () => buscarCandidatura(id as string),
    enabled: Boolean(id),
  });
}

export function useHistoricoCandidatura(id: string | undefined) {
  return useQuery({
    queryKey: RECRUTAMENTO_KEYS.candidaturaHistorico(id ?? 'noop'),
    queryFn: () => listarHistoricoCandidatura(id as string),
    enabled: Boolean(id),
  });
}

export function useAgendamentosCandidatura(id: string | undefined) {
  return useQuery({
    queryKey: RECRUTAMENTO_KEYS.candidaturaAgendamentos(id ?? 'noop'),
    queryFn: () => listarAgendamentos(id as string),
    enabled: Boolean(id),
  });
}

export function useMovimentarCandidatura() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: MoveApplicationPayload & { id: string }) =>
      movimentarCandidatura(id, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: RECRUTAMENTO_KEYS.all }),
  });
}

export function useAgendarEtapa() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: SchedulePayload & { id: string }) => agendarEtapa(id, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: RECRUTAMENTO_KEYS.all }),
  });
}

export function useAprovarCandidatura() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, parecer }: { id: string; parecer?: string }) =>
      aprovarCandidatura(id, parecer),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: RECRUTAMENTO_KEYS.all }),
  });
}

export function useReprovarCandidatura() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => reprovarCandidatura(id, reason),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: RECRUTAMENTO_KEYS.all }),
  });
}

export function useEnviarParaBancoTalentos() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      enviarParaBancoTalentos(id, reason),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: RECRUTAMENTO_KEYS.all }),
  });
}
